/**
 * Product Total Price Block
 * Updates total price display including upsell products
 * Calculates Afterpay (÷4) and Zip (÷6) installments
 */
(() => {
  const formatMoney = (cents, format, allowDecimals = false) => {
    // Custom formatting for $ no decimals by default
    const value = (cents || 0) / 100;
    
    if (allowDecimals) {
      // Format with commas and 2 decimals
      const formatted = value.toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return "$" + formatted;
    }

    // Format with commas, no decimals
    const formatted = Math.floor(value).toLocaleString("en-AU");
    return "$" + formatted;
  };

  const getSelectedVariant = () => {
    const node = document.querySelector(
      "variant-selects [data-selected-variant]"
    );
    if (!node) return null;
    try {
      return JSON.parse(node.innerHTML);
    } catch (_) {
      return null;
    }
  };

  const getMainProductPrice = () => {
    // Get wrapper for fallback compare price data
    const wrapper = document.querySelector("._1_pp_total_price_wrp");
    const wrapperComparePrice = wrapper
      ? parseInt(wrapper.dataset.comparePrice) || 0
      : 0;

    // Try to get from size select dropdown first (most reliable for custom variant pickers)
    const sizeSelect = document.querySelector('select[id^="Size-Select-"]');
    if (sizeSelect && sizeSelect.value) {
      const selectedOption = sizeSelect.querySelector(`option[value="${CSS.escape(sizeSelect.value)}"]`);
      if (selectedOption && selectedOption.dataset.price) {
        const priceText = selectedOption.dataset.price.replace(/[^0-9.]/g, '');
        const price = Math.round(parseFloat(priceText) * 100) || 0;
        let comparePrice = 0;
        if (selectedOption.dataset.regularPrice) {
          const compareText = selectedOption.dataset.regularPrice.replace(/[^0-9.]/g, '');
          comparePrice = Math.round(parseFloat(compareText) * 100) || 0;
        }
        if (price > 0) {
          return { price, comparePrice };
        }
      }

      // Get variant data from ShopifyAnalytics
      const variants = window.ShopifyAnalytics?.meta?.product?.variants;
      if (variants) {
        const variant = variants.find(
          (v) => v.id.toString() === sizeSelect.value.toString()
        );
        if (variant) {
          // Get compare price from variant or calculate from wrapper ratio
          let comparePrice = variant.compare_at_price || 0;

          // If no compare price in variant data, try to get from hidden select option
          if (!comparePrice) {
            const option = sizeSelect.querySelector(
              `option[value="${sizeSelect.value}"]`
            );
            if (option && option.dataset.comparePrice) {
              comparePrice = parseInt(option.dataset.comparePrice) || 0;
            }
          }

          // If still no compare price, calculate proportionally from initial wrapper data
          if (!comparePrice && wrapperComparePrice > 0) {
            const wrapperSalePrice = wrapper
              ? parseInt(wrapper.dataset.salePrice) || 0
              : 0;
            if (wrapperSalePrice > 0) {
              // Calculate the ratio and apply to current variant price
              const ratio = wrapperComparePrice / wrapperSalePrice;
              comparePrice = Math.round(variant.price * ratio);
            }
          }

          return {
            price: variant.price,
            comparePrice: comparePrice,
          };
        }
      }
    }

    // Try to get from variant-selects data
    const selectedVariant = getSelectedVariant();
    if (selectedVariant && selectedVariant.price) {
      return {
        price: selectedVariant.price,
        comparePrice: selectedVariant.compare_at_price || 0,
      };
    }

    // Fallback: get from the total price wrapper data attributes
    if (wrapper) {
      return {
        price: parseInt(wrapper.dataset.salePrice) || 0,
        comparePrice: wrapperComparePrice,
      };
    }

    return { price: 0, comparePrice: 0 };
  };

  const getUpsellTotalPrice = () => {
    let totalPrice = 0;
    let totalComparePrice = 0;

    // Get selected upsell variant IDs from the global store
    const selectedIds = window.selectedUpsellVariants
      ? Array.from(window.selectedUpsellVariants)
      : [];

    // Find the price for each selected upsell
    document.querySelectorAll("._1_pp_box[data-variants]").forEach((box) => {
      const btn = box.querySelector("._1_pp_add_btn");
      if (!btn) return;

      const variantId = btn.dataset.variationId;
      if (!variantId || !selectedIds.includes(variantId)) return;

      // Get variants data
      let variants;
      try {
        variants = JSON.parse(box.dataset.variants || "[]");
      } catch (e) {
        variants = [];
      }

      // Find the selected variant
      const variant = variants.find(
        (v) => v.id.toString() === variantId.toString()
      );
      if (variant) {
        totalPrice += variant.price || 0;
        totalComparePrice += variant.compare_at_price || variant.price || 0;
      }
    });

    return { price: totalPrice, comparePrice: totalComparePrice };
  };

  const updateTotalPriceDisplay = () => {
    const wrapper = document.querySelector("._1_pp_total_price_wrp");
    if (!wrapper) return;

    // Get main product price
    const mainProduct = getMainProductPrice();

    // Get upsell total
    const upsellTotal = getUpsellTotalPrice();

    // Calculate totals
    const totalPrice = mainProduct.price + upsellTotal.price;
    const totalComparePrice =
      mainProduct.comparePrice + upsellTotal.comparePrice;

    // Update sale price display
    const salePriceContainer = wrapper.querySelector("._1_pp_atc_price_sale");
    let salePriceEl = wrapper.querySelector("#product-price-all");
    if (!salePriceEl && salePriceContainer) {
      const existingText = salePriceContainer.textContent.trim();
      salePriceContainer.textContent = "";
      salePriceEl = document.createElement("div");
      salePriceEl.id = "product-price-all";
      salePriceEl.className = "_1_headline2";
      salePriceEl.textContent = existingText;
      salePriceContainer.appendChild(salePriceEl);
    }
    if (salePriceEl) {
      salePriceEl.textContent = formatMoney(totalPrice);
      salePriceEl.dataset.basePrice = (totalPrice / 100).toFixed(2);
    }

    // Update compare price display
    const comparePriceWrap = wrapper.querySelector("._1_pp_atc_price_rrp_wrp");
    const comparePriceEl = wrapper.querySelector("[data-compare-price]");
    if (comparePriceWrap && comparePriceEl) {
      if (totalComparePrice && totalComparePrice > totalPrice) {
        comparePriceEl.textContent = formatMoney(totalComparePrice);
        comparePriceWrap.style.display = "flex";
      } else {
        comparePriceWrap.style.display = "none";
      }
    }

    // Update Afterpay price (divide by 4)
    const afterpayEl = wrapper.querySelector("[data-afterpay-price]");
    if (afterpayEl) {
      const afterpayPrice = Math.ceil(totalPrice / 4);
      afterpayEl.innerHTML =
        formatMoney(afterpayPrice, null, true) + ' <span class="indicator_m">/ftn.</span>';
    }

    // Update Zip price (divide by 6)
    const zipEl = wrapper.querySelector("[data-zip-price]");
    if (zipEl) {
      const zipPrice = Math.ceil(totalPrice / 6);
      zipEl.innerHTML =
        formatMoney(zipPrice, null, true) + ' <span class="indicator_m">/mo.</span>';
    }
  };

  const init = () => {
    // Initial update
    updateTotalPriceDisplay();

    // Listen for variant changes
    document.addEventListener("variant:change", updateTotalPriceDisplay);

    // Listen for size dropdown changes
    document
      .querySelectorAll(
        'variant-selects input[name^="options["], variant-selects select[name^="options["], select[id^="Size-Select-"]'
      )
      .forEach((el) => {
        el.addEventListener("change", () => {
          // Small delay to allow variant data to update
          setTimeout(updateTotalPriceDisplay, 50);
        });
      });

    // Listen for upsell selection changes
    document.querySelectorAll("._1_pp_add_btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        // Small delay to allow upsell state to update
        setTimeout(updateTotalPriceDisplay, 50);
      });
    });

    // Also listen for custom event that upsell.js might dispatch
    document.addEventListener("upsell:change", updateTotalPriceDisplay);
  };

  // Expose update function globally for other scripts to call
  window.updateTotalPriceDisplay = updateTotalPriceDisplay;

  document.addEventListener("DOMContentLoaded", init);

  // Hook into Dawn pub-sub if available
  if (
    typeof subscribe !== "undefined" &&
    typeof PUB_SUB_EVENTS !== "undefined"
  ) {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      setTimeout(updateTotalPriceDisplay, 50);
    });
  }
})();
